import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../Services/translation';
import { post, get } from '../Services/http';
import { catchErrorNoDispatch } from '../Actions/Error.action';
import { handleApiErrors } from '../Services/Error.service';
import { addFlashMessage } from '../Actions/FlashMessages.action';
import { receive } from '../Actions/MyAccountMySubscriptions.action';

const MyAccountMySubscriptionsDetails = ({ onToggleManageSubscriptionMode }) => {
    const dispatch = useDispatch();
    const { payload } = useSelector(state => state.myAccountMySubscriptions);
    const [errors, setErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const changePaymentMethod = async () => {
        try {
            const response = await get('/api/products/subscriptions/paymentLink');
            const responseUrl = await response?.text();
            if (!responseUrl) {
                dispatch(addFlashMessage(
                    { 
                        messageText: translate('js.myaccountflowsubscriptions.paymentmethod.change.error'), 
                        messageType: "Pink", 
                        dismissible: true, 
                        timeToLive: 10 
                    }))
                return;
            }
            window.location.href = responseUrl;
        } catch (ex) {
            catchErrorNoDispatch(ex, (error) => {
                dispatch(handleApiErrors(error));
                setErrors(error);
            })
        }
    }

    const handleResumeSubscription = async () => {
        if (!payload?.flowSubscriptionSignupUrl || !payload?.currentOrLatestSubscriptionProductId) {
            try {
                await post(
                '/api/flashmessage/add/', 
                { 
                    messageText: translate('js.myaccountflowsubscriptions.general.error'), 
                    messageType: "Pink", 
                    dismissible: true, 
                    timeToLive: 10 
                });
            } catch (ex) {
            } finally {
                window.scrollTo(0,0);
                window.location.reload();
                return;
            }
        }
        try {
            const response = await get(`/api/products/subscriptions/resume/${payload.currentOrLatestSubscriptionProductId}`);
            const responseUrl = await response?.text();
            if (!responseUrl) {
                dispatch(addFlashMessage(
                    {
                        messageText: translate('js.myaccountflowsubscriptions.general.error'),
                        messageType: "Pink", 
                        dismissible: true, 
                        timeToLive: 10 
                    }
                ));
                return;
            }
            window.location.href = responseUrl;
        } catch (ex) {
            catchErrorNoDispatch(ex, (error) => {
                dispatch(handleApiErrors(error));
                setErrors(error);
            })
        }
    }

    const handleAbortCancellation = async () => {
        if (!payload?.currentOrLatestSubscriptionInfoId) { 
            try {
                await post(
                '/api/flashmessage/add/', 
                { 
                    messageText: translate('js.myaccountflowsubscriptions.managesubscription.abortcancellation.errortext'), 
                    messageType: "Pink", 
                    dismissible: true, 
                    timeToLive: 10 
                });
            } catch (ex) {
            } finally {
                window.scrollTo(0,0);
                window.location.reload();
                return;
            }
        }
        try {
            setIsLoading(true);
            const response = await post(`/api/products/subscriptions/${payload.currentOrLatestSubscriptionInfoId}/abort-cancellation`);
            const data = await response?.json();
            dispatch(receive(data));
        } catch (ex) {
            catchErrorNoDispatch(ex, (error) => {
                dispatch(handleApiErrors(error));
                setErrors(error);
            })
        } finally {
            setIsLoading(false);
        }
    }

    const getCurrentOrLatestSubscriptionContent = () => {
        if (!payload?.currentOrLatestSubscriptionProductId || payload.currentOrLatestSubscriptionProductId === '') {
            return (
                <Fragment>
                    <div>
                        <h4>{translate('js.myaccountflowsubscriptions.subscription.nosubscription.heading')}</h4>
                        <div>{translate('js.myaccountflowsubscriptions.subscription.nosubscription.text')}</div>
                    </div>
                    <div>
                        <a href={payload.flowSubscriptionProductPageUrl} className="button">
                            {translate('js.myaccountflowsubscriptions.subscription.nosubscription.buttontext')}
                        </a>
                    </div>
                </Fragment>);
        } else if (payload.currentOrLatestSubscriptionCancelDate) {
            if (payload.subscriptionNextPaymentDateHasPassed) {
                const expiredSubscriptionHeading = translate('js.myaccountflowsubscriptions.subscription.expiredsubscription.heading')?.replace('{0}', payload.currentOrLatestSubscriptionProductName)
                return (
                    <Fragment>
                        <div>
                            <h4>{expiredSubscriptionHeading}</h4>
                            <div>
                                {translate('js.myaccountflowsubscriptions.subscription.expiredsubscription.text')} {" "}
                                <a href={payload.flowSubscriptionProductPageUrl} className="button button__link">
                                    {translate('js.myaccountflowsubscriptions.subscription.expiredsubscription.linktext')}
                                </a>
                            </div>
                        </div>
                        <div>
                            <button onClick={handleResumeSubscription} className="button">
                                {translate('js.myaccountflowsubscriptions.subscription.expiredsubscription.buttontext')}
                            </button>
                        </div>
                    </Fragment>);
            } else {
                const expiringSubscriptionHeading = translate('js.myaccountflowsubscriptions.subscription.expiringsubscription.heading')?.replace('{0}', payload.currentOrLatestSubscriptionProductName)
                return (
                    <Fragment>
                        <div>
                            <h4>{expiringSubscriptionHeading} {payload.currentOrLatestSubscriptionNextPaymentDateFormatted}</h4>
                            <div>{translate('js.myaccountflowsubscriptions.subscription.expiringsubscription.text')}</div>
                        </div>
                        <div>
                            <button 
                                onClick={handleAbortCancellation} 
                                className={`button ${isLoading ? 'loading' : ''}`}
                                disabled={isLoading}>
                                {translate('js.myaccountflowsubscriptions.subscription.expiringsubscription.buttontext')}
                                <span className="spinner spinner--light spinner--small"></span>
                            </button>
                        </div>
                    </Fragment>);
            }
        } else {
            return (
                <Fragment>
                    <div>
                        <h4>{payload.currentOrLatestSubscriptionProductName}</h4>
                        <div>{payload.currentOrLatestSubscriptionProductDescription}</div>
                        <div className="my-account-flow-subscriptions__subscription__payment">
                            {translate('js.myaccountflowsubscriptions.subscription.nextpaymentdate.label')} {" "}
                            <span className="my-account-flow-subscriptions__subscription__payment--date">
                                {payload.currentOrLatestSubscriptionNextPaymentDateFormatted}
                            </span>
                        </div>
                    </div>
                    <div>
                        <button onClick={onToggleManageSubscriptionMode} className="button">
                            {translate('js.myaccountflowsubscriptions.subscription.manage.buttontext')}
                        </button>
                    </div>
                </Fragment>);
        }
    }

    const getCreditsContent = () => {
        if (payload.credits.length > 0)
        {
            return (
                <div className="my-account-flow-subscriptions__credits">
                    <div>
                        {payload.credits.map((c, i) => (
                            <div className={c.isTopTierCredits || payload.credits.length < 2 ? "" : "not-top-tier"} key={i}>
                                {c.Name && <h4>@.Name</h4>}
                                <div className="my-account-flow-subscriptions__credits__credits">
                                    <img src={c.iconImageUrl} /> {" "}
                                    <span>{c.amount}</span>
                                </div>
                                <div>{c.description}</div>
                            </div>
                        ))}
                    </div>
                    <a href={payload.creditStoreUrl} className="button">
                        {translate('js.myaccountflowsubscriptions.credits.store.buttontext')}
                    </a>
                </div>)
        }
    }

    const getPaymentMethodContent = () => {
        if (payload?.paymentMethodName && payload.paymentMethodName !== '') {
            return (
                <div className="my-account-flow-subscriptions__payment-method">
                    <h4>{translate('js.myaccountflowsubscriptions.paymentmethod.heading')}</h4>
                    <div>{translate('js.myaccountflowsubscriptions.paymentmethod.description')}</div>
                    {payload?.paymentMethodName === 'card' && <div>{payload.paymentMethodName}</div>}
                    {payload?.paymentMethodCardNumber && (
                        <div className="my-account-flow-subscriptions__payment-method__card">
                            <div>
                                <i className="icon-credit_card"></i>
                                <span className="my-account-flow-subscriptions__payment-method__card--bold">
                                    &bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&nbsp;&bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&nbsp;&bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&bull;&nbsp;&nbsp;{payload.paymentMethodCardNumber}
                                </span>
                            </div>
                            <div>
                                <i className="icon-clock"></i>
                                <span>{translate('js.myaccountflowsubscriptions.paymentmethod.card.expires.label')}</span>&nbsp;
                                <span className="my-account-flow-subscriptions__payment-method__card--bold">{payload.paymentMethodCardExpiration}</span>
                            </div>
                        </div>
                    )}
                    {payload?.paymentMethodText && <div>{payload.paymentMethodText}</div>}
                    {payload?.paymentMethodErrorText && <div className="my-account-flow-subscriptions__payment-method--error">{payload.paymentMethodErrorText}</div>}
                    <button onClick={changePaymentMethod} className="button button__link">
                        {translate('js.myaccountflowsubscriptions.paymentmethod.change.link.text')}
                    </button>
                </div>
            )
        }
    }

    return (
        <Fragment>
            <div className="my-account-flow-subscriptions__subscription">
                {getCurrentOrLatestSubscriptionContent()}
            </div>
            {getCreditsContent()}
            {getPaymentMethodContent()}
        </Fragment>
    )
    
}

export default MyAccountMySubscriptionsDetails;